// @flow
import * as React from 'react';
import { Box } from 'grommet';
import UpperContainer from 'esm-web-pro/components/layout/sidePanel/UpperContainer';
import Title from 'esm-web-pro/components/layout/sidePanel/Title';
import { withRouter } from 'react-router-dom';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import { connectAPI } from 'speed-js-react';
import HorizontalLine from 'esm-web-pro/components/layout/HorizontalLine';
import Attributes from 'esm-web-pro/components/layout/sidePanel/Attributes';
import type { Match, RouterHistory, Location } from 'react-router-dom';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import ButtonContainer from 'esm-web-pro/components/layout/sidePanel/ButtonContainer';
import { FormClose, FormCheckmark } from 'grommet-icons';
import { HTTP_404_NOT_FOUND } from 'speed-js-core';
import type { Nomenclature } from 'esm-web-pro/containers/map/types';
import type { SchemaType } from 'speed-js-react/src/components/forms/types';
import Spinner from 'esm-web-pro/components/forms/Spinner';
import SidePane from 'esm-web-pro/components/layout/SidePane';

type Props = {
    getNomenclatureItem: (itemCode: string) => Promise<Nomenclature>,
    getNomenclatureItemSchema: (itemCode: string) => Promise<SchemaType>,
    match: Match,
    intl: IntlShape,
    history: RouterHistory,
    location: Location,
    exclude?: Array<string>,
    url: string,
}

const ModelAttributes = (props: Props) => {
    const [model, setModel] = React.useState<Nomenclature | null>(null);
    const [fields, setFields] = React.useState(null);
    const [error, setError] = React.useState(null);

    const {
        match: { params: { modelCode } }, match, intl: { locale }, history, location, exclude, url,
    } = props;

    const getItemData = async () => {
        const modelResponse = await props.getNomenclatureItem(modelCode);
        if (modelResponse.ok === false) {
            setError(modelResponse);
        } else {
            setModel(modelResponse);
            const schemaResponse: SchemaType = await props.getNomenclatureItemSchema(modelCode);
            setFields(schemaResponse.fields);
        }
    };

    React.useEffect(() => {
        getItemData();
    }, []);

    const title = model && model.translations && model.translations[locale].name;
    const attributes = React.useMemo(() => {
        const _attributes = [];
        if (model && fields) {
            Object.keys(model).forEach((key) => {
                const field = fields.find((f) => f[0] === key)?.[1];
                if (field && (!exclude || !exclude.includes(key)) && key !== 'data') {
                    const { ui: { label }, type, write_only: writeOnly } = field;
                    if (writeOnly) return;
                    let value = model[key];

                    if (type === 'boolean') {
                        value = model[key] ? <FormCheckmark /> : <FormClose />;
                    } else if (type === 'translations') {
                        return;
                    }
                    _attributes.push({ label, value });
                }
            });
        }

        return _attributes;
    }, [model, fields, exclude]);

    const navigateBack = () => {
        if (location.state && location.state.from) {
            history.push(location.state.from);
        } else {
            history.push(match.url.split('/model')[0]);
        }
    };

    if (!model && !error) {
        return (
            <SidePane
                id="model-attributes"
                url={url}
                openInitially
            >
                <Spinner
                    type="clipLoader"
                    loading
                    blocking={false}
                    color="black"
                    coverStyle={{
                        position: 'relative',
                        width: 'auto',
                        height: '100%',
                    }}
                />
            </SidePane>
        );
    }

    return (
        <SidePane
            id="model-attributes"
            url={url}
            openInitially
            title={title}
        >
            <Box height="100%" justify="between">
                <Box>
                    {model && fields && (
                        <UpperContainer>
                            <HorizontalLine color="grey" />
                            <Attributes attributes={attributes} />
                            <HorizontalLine color="grey" />
                        </UpperContainer>
                    )}
                    {error && error.status === HTTP_404_NOT_FOUND
                        && (
                            <Box margin="small" align="center">
                                <span>
                                    <FormattedMessage
                                        id="model-not-fetched"
                                        defaultMessage="Model with the code {modelCode} can't be found"
                                        values={{ modelCode }}
                                    />
                                </span>
                            </Box>
                        )
                    }
                </Box>
                <ButtonContainer handleClick={navigateBack} />
            </Box>
        </SidePane>
    );
};

ModelAttributes.defaultProps = {
    exclude: ['template', 'created', 'modified'],
};

const apiCallback = (apiClient: ApiClient) => ({
    getNomenclatureItem: {
        send: (item) => apiClient.resources.nomenclatureItem.get(item),
    },
    getNomenclatureItemSchema: {
        send: (item) => apiClient.resources.nomenclatureItem.schema(item),
    },
});
export default injectIntl(connectAPI(apiCallback)(withRouter(ModelAttributes)));
